import { dbFor, type ConflictRow } from '../db.js'
import { enqueue, runCycle } from './engine.js'

/**
 * Résolution d'un conflit d'arbitrage (§7.6).
 *
 * Deux issues seulement, et c'est délibéré : garder sa version, ou garder
 * celle du serveur. Une fusion champ par champ à la main a été écartée — elle
 * offre une combinatoire dont personne n'a besoin au comptoir, et le serveur a
 * déjà fusionné tout ce qui pouvait l'être sans décision humaine.
 */

/** `payment_terms_days` (journal) → `paymentTermsDays` (saisie). */
function toCamel(field: string): string {
  return field.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
}

export async function resolveConflict(
  storeId: string,
  conflict: ConflictRow,
  choice: 'local' | 'server',
): Promise<void> {
  const db = dbFor(storeId)

  // La mutation d'origine a été refusée : dans les deux cas elle disparaît.
  await db.outbox.delete(conflict.id)

  if (choice === 'server') {
    // On abandonne la saisie locale. La ligne redevient propre pour que le
    // prochain delta puisse enfin y écrire l'état serveur, jusqu'ici bloqué
    // par le drapeau `_dirty`.
    const table = db.table(`${conflict.entity}s`)
    const local = await table.get(conflict.entityId)
    if (local) await table.put({ ...local, _dirty: 0 })
    await db.conflicts.delete(conflict.id)
    void runCycle(storeId)
    return
  }

  // « Garder ma version » : on rejoue la mutation en repartant de la version
  // SERVEUR. Sans ce rebasage, la nouvelle tentative serait refusée pour la
  // même raison que la première, indéfiniment.
  const payload: Record<string, unknown> = {}
  for (const field of conflict.fields) {
    const key = toCamel(field)
    payload[key] = conflict.localPayload[key]
  }
  // Les champs non conflictuels de la saisie initiale restent du voyage : ils
  // avaient été acceptés en fusion, les réémettre ne coûte rien et garantit
  // qu'aucune partie de la saisie n'est perdue en route.
  for (const [key, value] of Object.entries(conflict.localPayload)) {
    if (!(key in payload)) payload[key] = value
  }

  await enqueue(storeId, {
    entity: conflict.entity as 'customer' | 'item' | 'supplier',
    entityId: conflict.entityId,
    op: 'update',
    payload,
    baseVersion: conflict.serverVersion,
  })

  await db.conflicts.delete(conflict.id)
  void runCycle(storeId)
}

/** Abandonne toutes les mutations refusées et rend la main au serveur. */
export async function discardAllConflicts(storeId: string): Promise<void> {
  const db = dbFor(storeId)
  const all = await db.conflicts.toArray()
  for (const conflict of all) await resolveConflict(storeId, conflict, 'server')
}
